/** The campaign screen, wired.
 *
 * Everything the screen cannot do: build the campaign out of the generator,
 * read and write the player's progress, start a leg with its carry-over and
 * bonus applied, and notice when a leg has been won.
 *
 * The "noticing" is the part worth reading. A campaign leg ends the same way
 * any game does - `victory.over` goes true - so this watches the session for it
 * rather than being told. That means a leg won by any route counts: a capital
 * taken, a rival's last hero killed, or the stalemate breaker adjudicating a
 * war nobody could finish.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { CampaignFile, CampaignProgress, ContentRegistry } from "@game/core";
import type { CampaignBonusView, CampaignLegView, CarriedHeroView } from "@game/ui";
import { CampaignScreen, resourceDetail } from "@game/ui";

import { app } from "../game/app.js";
import { DEFAULT_OPTIONS } from "../game/newgame.js";
import {
  HUMAN, SHIPPED_CAMPAIGN_ID, advanceProgress, clearProgress, readProgress,
  shippedCampaign, startLeg, writeProgress,
} from "../game/campaign.js";

const NO_UNSUBSCRIBE = (): void => { /* nothing to unsubscribe from */ };

export interface CampaignRouteProps {
  readonly content: ContentRegistry | null;
  readonly onClose: () => void;
}

export function CampaignRoute(props: CampaignRouteProps): JSX.Element {
  const { content } = props;
  const [progress, setProgress] = useState<CampaignProgress>(() => readProgress());

  /**
   * Built once per content bundle. Four generated maps is a second of work and
   * it must not happen on every render of a screen with radio buttons on it.
   */
  const file = useMemo<CampaignFile | null>(
    () => (content === null ? null : shippedCampaign(content)), [content],
  );

  const legs = useMemo<readonly CampaignLegView[]>(() => {
    if (file === null) return [];
    return file.scenarios.map((s, i) => ({
      id: s.id,
      name: s.name,
      briefing: s.briefing,
      players: s.setup.players.length,
      cells: s.setup.map.width,
      carryHeroes: s.carry.heroes,
      bonuses: s.bonuses.map(bonusView),
      state: i < progress.at ? "done" as const
        : i === progress.at ? "next" as const : "later" as const,
    }));
  }, [file, progress.at]);

  const carried = useMemo<readonly CarriedHeroView[]>(() => {
    if (content === null) return [];
    return progress.carried.map((c) => ({
      name: content.heroes.get(c.def)?.name ?? (c.def as string),
      level: levelFor(content, c.experience),
      artifacts: c.artifacts.length,
    }));
  }, [content, progress.carried]);

  const begin = useCallback((bonusIndex: number): void => {
    if (content === null || file === null) return;
    const scenario = file.scenarios[progress.at];
    if (scenario === undefined) return;
    const state = startLeg(scenario, progress, content, bonusIndex, DEFAULT_OPTIONS.difficulty);
    // `resumeGame` rather than `newGame`: the state is already built, and it is
    // the path that reads who is playing out of the state.
    app.resumeGame(state, {
      ...DEFAULT_OPTIONS,
      seed: scenario.setup.rootSeed,
      mapCells: scenario.setup.map.width,
      opponents: scenario.setup.players.length - 1,
      humans: 1,
      victory: scenario.setup.victory ?? "defeat_all",
    });
  }, [content, file, progress]);

  const restart = useCallback((): void => {
    clearProgress();
    setProgress(readProgress());
  }, []);

  // ---- winning a leg

  const session = useSyncExternalStore(app.subscribe, app.getSnapshot).session;
  const over = useSyncExternalStore(
    useCallback((l: () => void) => (session === null ? NO_UNSUBSCRIBE : session.subscribe(l)), [session]),
    useCallback(() => session?.getState().victory.over ?? false, [session]),
  );
  /** The session a win has already been recorded for, so it counts once. */
  const recorded = useRef<unknown>(null);

  useEffect(() => {
    if (session === null || !over || file === null) return;
    if (recorded.current === session) return;
    const state = session.getState();
    // Only a win advances the road. Losing a leg leaves the progress where it
    // was, which is what lets a player try it again.
    if (state.victory.winner !== HUMAN) return;
    recorded.current = session;
    const scenario = file.scenarios[progress.at];
    if (scenario === undefined) return;
    const next = advanceProgress(progress, file, state, scenario.name);
    writeProgress(next);
    setProgress(next);
  }, [session, over, file, progress]);

  return (
    <CampaignScreen
      content={content}
      name={file?.meta.name ?? "Campaign"}
      description={file?.meta.description ?? (content === null ? "Loading." : "")}
      legs={legs}
      carried={carried}
      at={progress.at}
      onBegin={begin}
      onRestart={restart}
      onClose={props.onClose}
    />
  );
}

function bonusView(b: CampaignFile["scenarios"][number]["bonuses"][number]): CampaignBonusView {
  if (b.kind === "resources") {
    return { label: b.label, detail: resourceDetail(b.resources as Record<string, number>) };
  }
  if (b.kind === "artifact") return { label: b.label, detail: "Into your hero's backpack." };
  return { label: b.label, detail: "Into your hero's army." };
}

/** The level a carried hero's experience buys, for the "coming with you" list. */
function levelFor(content: ContentRegistry, experience: number): number {
  const t = content.levels.thresholds;
  let level = 1;
  for (let i = 1; i < t.length; i++) {
    if (experience >= (t[i] ?? 0)) level = i + 1; else break;
  }
  return level;
}

export { SHIPPED_CAMPAIGN_ID };
